import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import publicAxiosInstance from "../services/publicApi";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for fetching all services (Protected)
export const fetchAllServices = createAsyncThunk(
  "service/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.SERVICE.SELECT_ALL,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Services API Error:",
        error.response?.status,
        error.response?.data,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch services",
      );
    }
  },
);

// Thunk for fetching all services (Public - No Auth)
export const fetchAllServicesPublic = createAsyncThunk(
  "service/fetchAllPublic",
  async (_, { rejectWithValue }) => {
    try {
      const response = await publicAxiosInstance.get(
        API_ENDPOINTS.SERVICE.SELECT_ALL,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Public Services API Error:",
        error.response?.status,
        error.response?.data,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch public services",
      );
    }
  },
);

// Thunk for fetching Service Dropdown (Public - No Auth)
export const fetchServiceDropdownPublic = createAsyncThunk(
  "service/fetchDropdownPublic",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await publicAxiosInstance.get(
        `${API_ENDPOINTS.SERVICE.DROPDOWN}?branchId=${branchId}`,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Public Service Dropdown API Error:",
        error.response?.status || "No Response",
        error.response?.data || error.message,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch public service dropdown",
      );
    }
  },
);

// Thunk for fetching service dropdown data (if needed)
export const fetchServiceDropDown = createAsyncThunk(
  "service/fetchDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.SERVICE.DROPDOWN);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch service dropdown",
      );
    }
  },
);

// Thunk for fetching a service by ID
export const fetchServiceById = createAsyncThunk(
  "service/fetchById",
  async (serviceId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.SERVICE.SELECT,
        { id: serviceId }, // Adjust payload structure if different
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Service not found",
      );
    }
  },
);

// Thunk for adding a new service
export const addService = createAsyncThunk(
  "service/add",
  async (serviceData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.SERVICE.INSERT,
        serviceData,
      );
      dispatch(fetchAllServices());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add service",
      );
    }
  },
);

// Thunk for updating an existing service
export const updateService = createAsyncThunk(
  "service/update",
  async (serviceData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.SERVICE.UPDATE,
        serviceData,
      );
      dispatch(fetchAllServices());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update service",
      );
    }
  },
);

// Thunk for deleting a service
export const deleteService = createAsyncThunk(
  "service/delete",
  async (serviceId, { rejectWithValue, dispatch }) => {
    try {
      await axiosInstance.delete(
        `${API_ENDPOINTS.SERVICE.DELETE}?id=${serviceId}`,
      );
      dispatch(fetchAllServices());
      return serviceId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete service",
      );
    }
  },
);

const serviceSlice = createSlice({
  name: "service",
  initialState: {
    servicesList: [],
    serviceDropDown: [],
    pagination: null,
    selectedService: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearSelectedService: (state) => {
      state.selectedService = null;
    },
    clearServiceError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Services (Protected)
      .addCase(fetchAllServices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllServices.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.servicesList = apiData?.data || apiData || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllServices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch services";
      })
      // Fetch All Services Public
      .addCase(fetchAllServicesPublic.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllServicesPublic.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.servicesList = apiData?.data || apiData || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllServicesPublic.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch public services";
        console.error("❌ Public Services API Failed:", action.payload);
      })
      // Fetch Service DropDown Public
      .addCase(fetchServiceDropdownPublic.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchServiceDropdownPublic.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.serviceDropDown =
          apiData?.data || apiData || action.payload || [];
      })
      .addCase(fetchServiceDropdownPublic.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload || "Failed to fetch public service dropdown";
      })
      // Fetch Service DropDown
      .addCase(fetchServiceDropDown.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchServiceDropDown.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        // Adjust based on actual API structure mapping if needed
        state.serviceDropDown = apiData?.data || apiData || [];
      })
      .addCase(fetchServiceDropDown.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch service dropdown";
      })
      // Fetch By ID
      .addCase(fetchServiceById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchServiceById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedService = action.payload.data;
      })
      .addCase(fetchServiceById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Service
      .addCase(addService.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addService.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addService.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Service
      .addCase(updateService.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateService.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateService.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Service
      .addCase(deleteService.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteService.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteService.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedService, clearServiceError } = serviceSlice.actions;
export default serviceSlice.reducer;
